import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog-sticky-footer';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { Check } from 'lucide-react';
import { DialogClose } from '@components/ui/dialog';
import { useDialogUrlState } from '@/hooks/useDialogUrlState';
import { api } from '@/trpc/react';
import { useModelDoOver } from '@hooks/useModelDoOver';
import React from 'react';

interface DoOverDialogProps {
  onConfirm: () => void;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  hasDoOverCredits?: boolean;
  doOverCreditsCount?: number;
  isPending?: boolean;
  modelId?: string;
  isOpenProp?: boolean;
  defaultOpen?: boolean;
}

export function DoOverDialog({
  trigger,
  hasDoOverCredits = false,
  isPending = false,
  modelId,
  onOpenChange,
  isOpenProp,
  defaultOpen = false
}: DoOverDialogProps) {
  const model = api.model.get.useQuery(
    {
      id: modelId!
    },
    {
      enabled: !!modelId
    }
  );
  const { handleDoOver } = useModelDoOver({});
  const { isOpen: isUrlOpen, updateUrl } = useDialogUrlState({
    paramName: 'dialog',
    paramValue: 'do-over-learn-more'
  });

  const isControlled = isOpenProp !== undefined;
  const isOpen = isControlled ? isOpenProp : isUrlOpen;

  // Effect to set initial state for uncontrolled mode based on defaultOpen or URL
  React.useEffect(() => {
    if (!isControlled && defaultOpen && !isUrlOpen) {
      updateUrl(true);
    }
  }, [isControlled, defaultOpen, isUrlOpen, updateUrl]);

  const handleOpenChange = (open: boolean) => {
    if (onOpenChange) {
      onOpenChange(open);
    }
    if (!isControlled) {
      updateUrl(open);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="secondary" size="sm">
            Learn more
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="flex flex-col gap-0 p-0 sm:max-h-[min(640px,80vh)] sm:max-w-lg [&>button:last-child]:hidden">
        <div className="overflow-y-auto p-6">
          <div className="-mx-6 -mt-6 mb-6">
            <Image
              src="https://storage.prompt-hunt.workers.dev/cm73xixdt000b8fzth8jbxewj_1"
              alt="Do-Over Feature"
              width={1024}
              height={768}
              className="w-full h-auto object-cover"
            />
          </div>
          <DialogHeader className="contents space-y-0 text-left">
            <DialogTitle className="text-xl font-bold text-left mb-2">
              Upload new photos to improve your model
            </DialogTitle>
            {/* <DialogDescription className="p-6"></DialogDescription> */}
          </DialogHeader>
          <div className="">
            <div className="flex flex-col gap-2">
              <p className="text-sm mb-6">
                Want to improve your model's results? The Do-Over feature lets you retrain your model with a fresh set
                of photos.
              </p>
              <div className="text-xs text-muted-foreground font-medium uppercase">Do-Over includes:</div>

              <div className="text-sm">
                <ul className="space-y-2 pt-2 pb-4 ">
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-2 text-success" />
                    Discounted price of $14.99 <span className="ml-1 text-success">(50% off)</span>
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-2 text-success" />
                    Upload a new set of photos
                  </li>
                  <li className="flex items-center">
                    <Check className="w-4 h-4 mr-2 text-success" />A complimentary photo pack{' '}
                    <span className="ml-1 text-success">(40 photos)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="border-t border-border px-6 py-4 sm:grid sm:grid-cols-2 sm:gap-4">
          <DialogClose asChild>
            <Button variant="secondary" className="w-full">
              Not now
            </Button>
          </DialogClose>
          <Button
            onClick={() => handleDoOver(model.data!, model.data!.modelRepository?.name!)}
            disabled={isPending || model.isPending}
            className="w-full"
            variant="accent"
          >
            {isPending ? 'Processing...' : hasDoOverCredits ? 'Start Do-Over' : 'Retrain my model'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
